import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, finalize, map, shareReplay, tap } from "rxjs/operators";
import { EnvService } from "@gea-waldviertler-website/shop-lib";

interface CsrfTokenResponse {
  token: string
}

/*
 * Client half of the CSRF protection (see Angular's HttpClient XSRF guide:
 * https://angular.dev/best-practices/security#httpclient-xsrf-csrf-security).
 *
 * Because the webshop and the gea-api live on different sites, the documented
 * cookie-to-header variant is not possible (JS on the shop origin cannot read a
 * cookie set by the api domain). Instead we fetch a signed token from the API
 * and echo it back in the "X-XSRF-TOKEN" header via HttpXsrfInterceptor.
 *
 * The token is fetched once and cached for the app session. A failed fetch is
 * not cached, so a later attempt retries.
 */
@Injectable()
export class CsrfService {

  private http = inject(HttpClient)
  private envService = inject(EnvService)

  private token?: string
  private inflight?: Observable<string>

  getToken(): Observable<string> {
    if (this.token) {
      return of(this.token)
    }

    if (!this.inflight) {
      const url = this.envService.config.apiURL + "/customer/csrf"
      this.inflight = this.http.get<CsrfTokenResponse>(url).pipe(
        map(response => response?.token ?? ''),
        tap(token => { if (token) this.token = token }),
        catchError(() => of('')),
        finalize(() => { this.inflight = undefined }),
        shareReplay(1)
      )
    }

    return this.inflight
  }

  // drop the cached token (e.g. after a 403), so the next request fetches a fresh one
  clear() {
    this.token = undefined
    this.inflight = undefined
  }
}
